import * as React from "react";
import { getCurrentTenant, type LoginResponse, type MeResponse } from "@eduviaos/shared";
import { api, onSessionExpired } from "./api";
import { portalUserFromUser } from "./portalUser";
import {
  SessionContext,
  type PortalLoginInput,
  type PortalUser,
  type SessionContextValue,
} from "./sessionContext";

/**
 * SessionProvider — real, JWT-backed learner/guardian identity (Phase
 * SC-3b). Replaces the P1 OTP mock. The access token lives in memory
 * inside the shared API client (`lib/api`); the refresh token is the
 * HttpOnly `eos_refresh` cookie the BE sets on `/auth/login`.
 *
 * Boot: replay the refresh cookie (`/auth/refresh`) → mint an access
 * token → fetch `/auth/me`. Success ⇒ authenticated. Any failure (no
 * cookie, expired, wrong tenant) ⇒ anonymous, and the route guard sends
 * the user to `/login`.
 *
 * Cross-tenant cookie defense: the `eos_refresh` cookie is scoped to
 * `.eduviaos.com` so the browser replays it on every subdomain. The BE
 * refuses `/auth/refresh` when the cookie's tenant claim doesn't match the
 * host slug; we ALSO assert client-side that the `/auth/me` principal's
 * tenant matches the host before mounting, so a brief deploy skew can't
 * render another tenant's data for even a frame.
 */

function tenantMatchesHost(userTenantId: string): boolean {
  const slug = getCurrentTenant().slug;
  // No host slug (local dev / bare host) — nothing to compare against;
  // trust the BE, which has the same fallback posture.
  if (!slug) return true;
  return slug.toLowerCase() === userTenantId.toLowerCase();
}

async function clearCrossTenantCookie(): Promise<void> {
  api.setAccessToken(null);
  try {
    await api.post("/auth/logout");
  } catch {
    // non-fatal
  }
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<PortalUser | null>(null);
  const [status, setStatus] = React.useState<SessionContextValue["status"]>("loading");

  const dropSession = React.useCallback(() => {
    api.setAccessToken(null);
    setUser(null);
    setStatus("anonymous");
  }, []);

  // Boot: silent refresh → /auth/me.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const refreshed = await api.post<{ access_token: string }>("/auth/refresh");
        api.setAccessToken(refreshed.access_token);
        const me = await api.get<MeResponse>("/auth/me");
        if (cancelled) return;
        if (!tenantMatchesHost(me.user.tenant_id)) {
          // Poisoned cross-tenant cookie — best-effort clear + drop.
          try {
            await api.post("/auth/logout");
          } catch {
            // non-fatal
          }
          if (!cancelled) dropSession();
          return;
        }
        setUser(portalUserFromUser(me.user));
        setStatus("authenticated");
      } catch {
        if (!cancelled) {
          api.setAccessToken(null);
          setUser(null);
          setStatus("anonymous");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dropSession]);

  // A dead refresh chain mid-session (expired/revoked cookie) flips us to
  // anonymous so the guard bounces to /login.
  React.useEffect(() => {
    onSessionExpired(() => dropSession());
    return () => onSessionExpired(null);
  }, [dropSession]);

  const login = React.useCallback(async (input: PortalLoginInput) => {
    const body: Record<string, string> = {
      email: input.email,
      password: input.password,
    };
    // Only forward tenant in the body when the host can't carry it, so a
    // body value never disagrees with the subdomain on per-tenant hosts.
    if (input.tenantId && !getCurrentTenant().slug) {
      body.tenant_id = input.tenantId;
    }
    const resp = await api.post<LoginResponse>("/auth/login", body);
    if (!tenantMatchesHost(resp.user.tenant_id)) {
      await clearCrossTenantCookie();
      throw new Error("This account belongs to a different institution.");
    }
    api.setAccessToken(resp.tokens.access_token);
    setUser(portalUserFromUser(resp.user));
    setStatus("authenticated");
  }, []);

  const mountLoginResponse = React.useCallback(
    async (resp: LoginResponse, mismatchMessage: string) => {
      if (!tenantMatchesHost(resp.user.tenant_id)) {
        await clearCrossTenantCookie();
        throw new Error(mismatchMessage);
      }
      api.setAccessToken(resp.tokens.access_token);
      setUser(portalUserFromUser(resp.user));
      setStatus("authenticated");
    },
    [],
  );

  const acceptInvite = React.useCallback(
    async (input: { token: string; password: string }) => {
      const resp = await api.post<LoginResponse>(
        `/onboarding/learner-invite/${encodeURIComponent(input.token)}/accept`,
        { password: input.password },
      );
      await mountLoginResponse(resp, "This invite belongs to a different institution.");
    },
    [mountLoginResponse],
  );

  const verifyMagicLink = React.useCallback(
    async (token: string) => {
      const resp = await api.get<LoginResponse>(
        `/auth/magic-link/verify?token=${encodeURIComponent(token)}`,
      );
      await mountLoginResponse(resp, "This sign-in link belongs to a different institution.");
    },
    [mountLoginResponse],
  );

  const signOut = React.useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Network / 401 on logout is non-fatal — proceed with local cleanup.
    }
    dropSession();
  }, [dropSession]);

  const refreshProfile = React.useCallback(async () => {
    const me = await api.get<MeResponse>("/auth/me");
    if (!tenantMatchesHost(me.user.tenant_id)) {
      dropSession();
      return;
    }
    setUser(portalUserFromUser(me.user));
    setStatus("authenticated");
  }, [dropSession]);

  const value = React.useMemo<SessionContextValue>(
    () => ({ user, status, login, acceptInvite, verifyMagicLink, signOut, refreshProfile }),
    [user, status, login, acceptInvite, verifyMagicLink, signOut, refreshProfile],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
